import React from 'react';
import { Info } from 'lucide-react';
import { LegalDocument, PageHero } from '../components/page';
import SmartLink from '../components/SmartLink';

const EMAIL = 'support@groupmoney.app';

const MailLink = () => <a href={`mailto:${EMAIL}`}>{EMAIL}</a>;

// Highlighted note inside a legal section.
const Callout = ({ title, children }) => (
  <div className="!mt-6 flex gap-3 rounded-2xl border border-brand-400/20 bg-brand-400/[0.06] p-5">
    <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" />
    <div>
      <p className="font-semibold text-white">{title}</p>
      <p className="mt-1 text-slate-300">{children}</p>
    </div>
  </div>
);

const Address = () => (
  <p className="text-slate-300">
    Groupmoney
    <br />
    3-425 McArthur Ave.
    <br />
    Ottawa, ON, Canada
  </p>
);

const SECTIONS = [
  {
    id: 'who-we-are',
    title: '1. Who We Are',
    content: (
      <>
        <p>
          Groupmoney is a platform that helps groups pool money for shared goals, such as trips, household expenses, or events. We're based at:
        </p>
        <Address />
        <p>We operate under Canadian law and are committed to handling your personal information responsibly.</p>
      </>
    ),
  },
  {
    id: 'information-we-collect',
    title: '2. What Information We Collect',
    content: (
      <>
        <p>We collect information to provide and improve our services. This includes:</p>
        <h3>Personal Information You Provide:</h3>
        <ul>
          <li>
            <strong>When you sign up:</strong> Name, email address, phone number (optional), and payment details (e.g., bank account or credit card information for transactions).
          </li>
          <li>
            <strong>When you create or join a group:</strong> Group names, member details (e.g., names, emails of invited members), and transaction details (e.g., contributions, withdrawals).
          </li>
          <li>
            <strong>When you contact us:</strong> Information shared via the "Resolution Center," support emails, or feedback forms (e.g., details about conflicts or inquiries).
          </li>
          <li>
            <strong>When setting a "Continuity Directive":</strong> Successor details (e.g., name, contact info) for post-death account management.
          </li>
        </ul>
        <h3>Automatically Collected Information:</h3>
        <ul>
          <li>
            <strong>Usage Data:</strong> IP address, device type, browser type, pages visited, and timestamps of activities (e.g., when you log in or make a transaction).
          </li>
          <li>
            <strong>Cookies and Tracking:</strong> We use cookies to enhance your experience (e.g., remembering login details). You can manage cookie preferences in your browser settings.
          </li>
        </ul>
        <h3>Information from Partners:</h3>
        <p>
          If you use Group Discounts (once available), we may receive data from partners (e.g., transaction confirmation for a discounted booking).
        </p>
        <p>We only collect what's necessary to provide our services, and we'll always ask for your consent where required.</p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    content: (
      <>
        <p>We use your information to:</p>
        <h3>Provide Our Services:</h3>
        <ul>
          <li>Process transactions (e.g., deposits, withdrawals, e-transfers).</li>
          <li>Manage groups and accounts (e.g., apply "Group Rules" like "Group Controls").</li>
          <li>Offer features like "Progress Tracking," AI Suggestions (Max tier), and Group Discounts (Basic/Max tiers, once available).</li>
          <li>Facilitate conflict resolution through the "Resolution Center" (e.g., mediate disputes with your consent).</li>
        </ul>
        <h3>Improve Our Platform:</h3>
        <ul>
          <li>Analyze usage data to enhance functionality, fix bugs, and develop new features (e.g., improving AI Suggestions).</li>
        </ul>
        <h3>Communicate with You:</h3>
        <ul>
          <li>Send transactional emails (e.g., contribution reminders, group updates).</li>
          <li>Respond to support inquiries or "Resolution Center" requests.</li>
          <li>Share updates about Groupmoney (e.g., new features, partnerships), with an option to opt out.</li>
        </ul>
        <h3>Ensure Security and Compliance:</h3>
        <ul>
          <li>Detect and prevent fraud or unauthorized access.</li>
          <li>Comply with legal obligations (e.g., tax reporting, anti-money laundering laws).</li>
        </ul>
        <h3>Support Partnerships:</h3>
        <ul>
          <li>Once Group Discounts launch, share necessary data with partners (e.g., transaction IDs for discount validation) with your consent.</li>
        </ul>
        <p>
          We limit the use of your information to these purposes and will not use it for unrelated activities without your explicit consent.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-share',
    title: '4. How We Share Your Information',
    content: (
      <>
        <p>We respect your privacy and only share your information in the following cases:</p>
        <h3>Within Your Group</h3>
        <p>
          Group members can see transaction details (e.g., who contributed, withdrew funds) to ensure transparency, as per our core feature of "Complete Visibility."
        </p>
        <h3>With Service Providers</h3>
        <p>
          We work with trusted third parties (e.g., payment processors, cloud hosting providers) to deliver our services. They are bound by strict confidentiality agreements and only access data necessary for their role.
        </p>
        <h3>For Conflict Resolution</h3>
        <p>
          If you use the "Resolution Center," our team may access relevant group data (e.g., transaction logs, member details) to mediate disputes. This information remains confidential and is only used for resolution purposes.
        </p>
        <h3>With Partners (Future)</h3>
        <p>
          Once Group Discounts are live, we may share minimal data (e.g., transaction IDs) with partners to validate discounts, but only with your consent at the time of use.
        </p>
        <h3>Legal Requirements</h3>
        <p>
          We may disclose information if required by law (e.g., court orders, tax authorities) or to protect Groupmoney's rights, safety, or property (e.g., fraud prevention).
        </p>
        <Callout title="Important Note">We will never sell your personal information to third parties for marketing or other purposes.</Callout>
      </>
    ),
  },
  {
    id: 'how-we-protect',
    title: '5. How We Protect Your Information',
    content: (
      <>
        <p>We take security seriously and implement measures to safeguard your data:</p>
        <h3>Encryption</h3>
        <p>All data is encrypted in transit (using SSL/TLS) and at rest (using AES-256).</p>
        <h3>Access Controls</h3>
        <p>Only authorized staff can access your information, and only for specific purposes.</p>
        <h3>Regular Audits</h3>
        <p>We conduct security audits and monitor for vulnerabilities.</p>
        <h3>Privacy in Mediation</h3>
        <p>"Resolution Center" discussions are confidential, with no details shared outside the mediation process.</p>
        <Callout title="Note">
          Despite our best efforts, no system is 100% secure. If a breach occurs, we'll notify you promptly as required by PIPEDA.
        </Callout>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: '6. Your Rights and Choices',
    content: (
      <>
        <p>Under PIPEDA, you have rights regarding your personal information:</p>
        <h3>Access</h3>
        <p>You can request a copy of the data we hold about you (e.g., transaction history, profile details).</p>
        <h3>Correction</h3>
        <p>If your information is inaccurate, you can ask us to correct it (e.g., update your email).</p>
        <h3>Deletion</h3>
        <p>You can request deletion of your account and data, subject to legal obligations (e.g., retaining transaction records for tax purposes).</p>
        <h3>Consent Withdrawal</h3>
        <p>You can withdraw consent for non-essential data use (e.g., marketing emails) at any time.</p>
        <h3>Opt-Out</h3>
        <p>Unsubscribe from promotional emails via the link in each message.</p>
        <Callout title="How to Exercise Your Rights">
          To exercise these rights, email <MailLink /> with your request. We'll respond within 30 days, as required by PIPEDA.
        </Callout>
      </>
    ),
  },
  {
    id: 'data-retention',
    title: '7. Data Retention',
    content: (
      <>
        <p>We keep your information only as long as necessary:</p>
        <h3>Active Accounts</h3>
        <p>Data is retained while your account is active to provide services.</p>
        <h3>Closed Accounts</h3>
        <p>We retain data for 2 years after account closure to comply with legal requirements (e.g., tax laws), then securely delete it.</p>
        <h3>Transaction Records</h3>
        <p>Kept for 7 years to meet Canadian tax and anti-money laundering regulations.</p>
        <h3>Resolution Center Data</h3>
        <p>Conflict resolution records are deleted 1 year after resolution, unless you request otherwise.</p>
      </>
    ),
  },
  {
    id: 'international-transfers',
    title: '8. International Data Transfers',
    content: (
      <>
        <p>
          Groupmoney is based in Canada, but we may use service providers (e.g., cloud hosting) located outside Canada (e.g., the U.S.). When data is transferred internationally, we ensure it's protected under agreements that meet PIPEDA standards (e.g., EU-US Privacy Framework for U.S. providers). By using our services, you consent to such transfers where necessary.
        </p>
        <h3>Our Data Protection Measures</h3>
        <p>
          Whenever we transfer your data internationally, we implement additional safeguards to ensure it receives a level of protection equivalent to that required under Canadian law. This includes:
        </p>
        <ul>
          <li>Using service providers certified under recognized data protection frameworks</li>
          <li>Implementing contractual measures (e.g., Standard Contractual Clauses) that legally bind service providers to protect your data</li>
          <li>Regular assessments of our partners' data protection practices</li>
        </ul>
      </>
    ),
  },
  {
    id: 'cookies',
    title: '9. Cookies and Tracking',
    content: (
      <>
        <p>We use cookies to improve your experience:</p>
        <h3>Essential Cookies</h3>
        <p>Enable core functionality (e.g., staying logged in).</p>
        <h3>Analytics Cookies</h3>
        <p>Help us understand usage (e.g., which pages are popular).</p>
        <h3>Marketing Cookies</h3>
        <p>Support future targeted promotions (e.g., Group Discounts).</p>
        <h3>Managing Cookie Preferences</h3>
        <p>You can manage cookie preferences in your browser settings or opt out of non-essential cookies via our cookie banner.</p>
      </>
    ),
  },
  {
    id: 'childrens-privacy',
    title: "10. Children's Privacy",
    content: (
      <>
        <h3>Age Restrictions</h3>
        <p>
          Groupmoney is not intended for users under 19 years of age, in line with PIPEDA's guidelines for consent in Canada. We do not knowingly collect information from children. If we discover such data, we will delete it immediately.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content: (
      <>
        <p>
          We may update this Privacy Policy to reflect changes in our services or legal requirements. We'll notify you of significant updates via email or an in-app notice at least 30 days before they take effect. Your continued use of Groupmoney after updates signifies your acceptance of the revised policy.
        </p>
        <h3>Policy Update History</h3>
        <div className="flex flex-col gap-1 rounded-2xl border border-white/[0.06] bg-ink-900/80 p-5 sm:flex-row sm:gap-6">
          <p className="shrink-0 font-semibold tabular-nums text-brand-300">March 13, 2025</p>
          <p className="!mt-0 text-slate-300">Initial Privacy Policy published.</p>
        </div>
      </>
    ),
  },
  {
    id: 'contact-us',
    title: '12. Contact Us',
    content: (
      <>
        <p>If you have questions, concerns, or requests about your privacy, please reach out:</p>
        <h3>Email</h3>
        <p>
          <MailLink />
        </p>
        <h3>Mail</h3>
        <Address />
        <h3>In-App</h3>
        <p>
          Log in, go to your profile, and visit the "<SmartLink to="/resolution-center">Resolution Center</SmartLink>" for support.
        </p>
        <h3>Privacy Regulatory Authority</h3>
        <p>
          If you're not satisfied with our response, you can contact the Office of the Privacy Commissioner of Canada at 1-800-282-1376 or via their website (<SmartLink to="https://www.priv.gc.ca">www.priv.gc.ca</SmartLink>).
        </p>
        <p className="border-t border-white/[0.06] pt-6 text-sm text-slate-500">
          This Privacy Policy was last updated on March 13, 2025 and is effective as of that date.
        </p>
      </>
    ),
  },
];

const PrivacyPage = () => (
  <>
    <PageHero
      eyebrow="Legal"
      meta="Last Updated: March 13, 2025"
      title="Privacy Policy"
      description="At Groupmoney, we're committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, disclose, and safeguard your data in compliance with Canadian laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA). We prioritize trust and transparency in everything we do, especially when it comes to your information."
    >
      <p className="max-w-2xl text-base text-slate-500">
        By using Groupmoney's services, you consent to the practices described in this policy. If you have any questions, please contact us at{' '}
        <a href={`mailto:${EMAIL}`} className="font-medium text-brand-300 underline-offset-4 hover:underline">
          {EMAIL}
        </a>
        .
      </p>
    </PageHero>
    <LegalDocument sections={SECTIONS} />
  </>
);

export default PrivacyPage;
